import { useCallback, useEffect, useState } from "react";
import { AppShell, type NavKey } from "@/components/AppShell";
import { Dashboard } from "@/pages/Dashboard";
import { QuickEntry } from "@/pages/QuickEntry";
import { Login } from "@/pages/Login";
import { Payroll } from "@/pages/Payroll";
import { PaidLeave } from "@/pages/PaidLeave";
import { H2a } from "@/pages/H2a";
import { Hours } from "@/pages/Hours";
import { Labour } from "@/pages/Labour";
import { ReportsPayrollRegister } from "@/pages/ReportsPayrollRegister";
import { ReportsScheduleF } from "@/pages/ReportsScheduleF";
import { ReportsProductionSales } from "@/pages/ReportsProductionSales";
import { ReportsVendorPurchases } from "@/pages/ReportsVendorPurchases";
import { ReportsLivestockInventory } from "@/pages/ReportsLivestockInventory";
import { ReportsCropProduction } from "@/pages/ReportsCropProduction";
import { ReportsCashFlow } from "@/pages/ReportsCashFlow";
import { ReportsAging } from "@/pages/ReportsAging";
// ef-053-0a: five stub pages pre-registered as scaffolding (D1, `ef-053-dispatch.md`) —
// each renders an honest "not built" state until its own builder replaces the file.
import { ReportsIncomeStatement } from "@/pages/ReportsIncomeStatement";
import { ReportsGrainInventory } from "@/pages/ReportsGrainInventory";
import { ReportsMilkProduction } from "@/pages/ReportsMilkProduction";
import { ReportsFieldApplications } from "@/pages/ReportsFieldApplications";
import { ReportsPartnerSplits } from "@/pages/ReportsPartnerSplits";
// ef-054-0a: five more stub pages pre-registered as scaffolding (D1,
// `ef-054-dispatch.md`) — each renders an honest "not built" state until its own
// builder replaces the file.
import { ReportsPartnerSplitsDollar } from "@/pages/ReportsPartnerSplitsDollar";
import { ReportsGlDetail } from "@/pages/ReportsGlDetail";
import { ReportsTrialBalance } from "@/pages/ReportsTrialBalance";
import { ReportsCashReceipts } from "@/pages/ReportsCashReceipts";
import { ReportsPayrollTaxLiability } from "@/pages/ReportsPayrollTaxLiability";
import { ReportsActivity } from "@/pages/ReportsActivity"; // ef-056-0a stub, ef-056-5b replaces
// ef-055-0a: two more stub pages pre-registered as scaffolding (D1, `ef-055-dispatch.md`)
// — each renders an honest "not built" state until its own builder replaces the file.
import { ReportsReconciliation } from "@/pages/ReportsReconciliation";
import { ReportsCropPlanting } from "@/pages/ReportsCropPlanting";
import { Places } from "@/pages/Places";
import { JobCodes } from "@/pages/JobCodes";
import { Schedule } from "@/pages/Schedule";
import { TimeOff } from "@/pages/TimeOff";
import { PayRuns } from "@/pages/PayRuns";
import { PayRunPrint } from "@/pages/PayRunPrint";
import { RegisterCheckPrint } from "@/pages/RegisterCheckPrint";
import { ReportsPicker } from "@/pages/ReportsPicker";
import { ReportsStatements } from "@/pages/ReportsStatements";
import { YearEnd } from "@/pages/YearEnd";
import { Access } from "@/pages/Access";
import { Books } from "@/pages/Books";
import { BooksVendors } from "@/pages/BooksVendors";
import { BooksRecurring } from "@/pages/BooksRecurring";
import { BooksReconcile } from "@/pages/BooksReconcile";
import { Farm } from "@/pages/Farm";
import { Livestock } from "@/pages/Livestock";
import { Fields } from "@/pages/Fields";
import { Grain } from "@/pages/Grain";
import { Milk } from "@/pages/Milk";
import { Equipment } from "@/pages/Equipment";
import { TimeClock } from "@/pages/TimeClock";
import { MultiEntityRollup } from "@/concept/MultiEntityRollup";
import { useSession } from "@/hooks/useSession";
import { StateMessage } from "@/components/StateMessage";

/**
 * Routing across the app's shell — five tiles (each its own area, ADR 0009 §1) plus
 * Setup, the two shell-less screens (`/entry`, `/time`), and the multi-entity concept
 * screen.
 *
 * Still hand-rolled rather than a router dependency: what ADR 0009 changes is not
 * "params" in the sense a library exists for — it's that a handful of areas now each
 * hold a small, closed set of TAB slugs rather than being a single leaf path.
 * `normalize()` below still resolves a pathname to exactly one canonical string; the
 * areas just parse one more segment off it.
 *
 * Retired paths are handled as two DIFFERENT things, and conflating them was the trap:
 *
 *   - LEGACY ALIASES (`/hours`, `/pay-runs`, `/year-end`, `/h2a`, `/paid-leave`,
 *     `/places`, `/job-codes`, `/access`, `/settings`, `/capture`) are old, no-longer-
 *     canonical addresses. A fresh load or the browser's own back/forward through one of
 *     these REWRITES the address bar to the new canonical URL (`resolvePath()` below) —
 *     "nothing bookmarked breaks" (ADR 0009 §2) means the redirect actually happens in
 *     the bar, not just that equivalent content renders under the old address
 *     (`redirects.spec.ts` asserts the URL itself, not just what's on screen).
 *   - A handful of pages this job's file scope does not extend to the BODY of still call
 *     `onNavigate("/year-end", …)` / `onNavigate("/paid-leave")` internally (H2a.tsx's
 *     "where this lands on the returns" links, YearEnd.tsx's paid-leave tile) — exactly
 *     the call sites two unchanged specs (`h2a.spec.ts`, `paid-leave.spec.ts`) still
 *     assert land on those literal old URLs. `navigate()` below therefore does NOT
 *     rewrite these — it pushes whatever path it's given verbatim — while `pageFor`
 *     (inlined below) still renders the right screen for either spelling, by running the
 *     SAME `normalize()` a caller's raw path might need. "What's redirected" and "what's
 *     rendered" are different questions and get different answers on purpose.
 *
 * `AreaTabs` (components/AreaTabs.tsx) is deliberately NOT part of this — it drives the
 * History API directly and reuses the `popstate` listener below rather than taking a
 * `navigate` prop, so adding tabs to five areas touched none of this file's plumbing.
 *
 * `/entry` is the one deliberate exception to "every screen reachable from every other
 * screen" (QuickEntry.tsx, no shell) and `/time` is the employee's own single screen
 * (TimeClock.tsx, no shell either) — both unchanged by this job; see the role/route
 * branches below.
 */

/** Every area with its own tab strip (ADR 0009 §2). */
const AREA_PATHS = ["/books", "/payroll", "/farm", "/reports", "/setup"] as const;

/**
 * Old addresses ADR 0009 §2 retires. Each still resolves — "nothing bookmarked breaks" —
 * to the tab that carries the same content today.
 */
const LEGACY_ALIASES: Record<string, string> = {
  "/hours": "/reports/hours",
  "/pay-runs": "/payroll/runs",
  "/year-end": "/payroll/returns",
  "/h2a": "/payroll/people",
  "/paid-leave": "/payroll/leave",
  "/places": "/setup/places",
  "/job-codes": "/setup/job-codes",
  "/access": "/setup/logins",
  "/settings": "/setup/logins",
  // The retired Capture screen — its scan box now lives on the Dashboard.
  "/capture": "/",
  // ef-056-5a (ADR 0010 §1) — Books drops its tab strip; "Entry" IS the page now.
  "/books/entry": "/books",
  // ef-056-5b (ADR 0010 §1) — Books' cross-account Activity view relocates to Reports.
  "/books/activity": "/reports/activity",
};

function stripTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

/**
 * Maps a raw pathname to the canonical string this app renders content for. Applies
 * legacy-alias resolution and otherwise passes any recognized area (bare, or with a tab
 * segment) through unchanged; anything else falls back to Home, exactly as before.
 */
function normalize(pathname: string): string {
  const trimmed = stripTrailingSlash(pathname);
  const aliased = LEGACY_ALIASES[trimmed];
  if (aliased) return aliased;
  if (
    trimmed === "/"
    || trimmed === "/entry"
    || trimmed === "/time"
    || trimmed === "/concept/rollup"
    || AREA_PATHS.some((area) => trimmed === area || trimmed.startsWith(`${area}/`))
  ) {
    return trimmed;
  }
  return "/";
}

/**
 * Resolves the CURRENT browser location for a hard load or a `popstate` (the browser's
 * own back/forward) — and, unlike `normalize()` alone, physically rewrites the address
 * bar when the path it was given is one of the retired `LEGACY_ALIASES`. That rewrite is
 * the whole of what `redirects.spec.ts` checks: the retired path must be GONE from the
 * bar, not merely rendering equivalent content underneath it.
 */
function resolvePath(): string {
  const raw = window.location.pathname;
  const trimmed = stripTrailingSlash(raw);
  const canonical = normalize(raw);
  if (trimmed in LEGACY_ALIASES && canonical !== trimmed) {
    window.history.replaceState({}, "", canonical);
  }
  return canonical;
}

function navKeyFor(route: string): NavKey {
  const canonical = normalize(route);
  if (canonical === "/") return "dashboard";
  if (canonical === "/books" || canonical.startsWith("/books/")) return "books";
  if (canonical === "/payroll" || canonical.startsWith("/payroll/")) return "payroll";
  if (canonical === "/farm" || canonical.startsWith("/farm/")) return "farm";
  if (canonical === "/reports" || canonical.startsWith("/reports/")) return "reports";
  if (canonical === "/setup" || canonical.startsWith("/setup/")) return "setup";
  // /entry never reaches AppShell; /time and /concept/rollup have no tile of their own —
  // Home is the closest honest default, same as before ADR 0009.
  return "dashboard";
}

export function App() {
  const [route, setRoute] = useState<string>(resolvePath);

  useEffect(() => {
    // Handles the browser's own back/forward buttons AND `AreaTabs`' synthetic dispatch
    // (pushState doesn't fire popstate on its own) — the one place either kind of route
    // change is observed. Refresh is a full reload, which naturally re-resolves the path
    // via useState's initializer above.
    function onPopState(): void {
      setRoute(resolvePath());
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  /**
   * Which return /payroll/returns should open on, when the caller asked for a specific
   * one. Set by navigate() and consumed by YearEnd's initialView — see its prop docs for
   * why this is a prop and not a URL fragment.
   */
  const [yearEndView, setYearEndView] = useState<"return" | "940" | "w2" | undefined>(undefined);

  const navigate = useCallback((path: string, view?: "return" | "940" | "w2") => {
    setYearEndView(view);
    setRoute((current) => {
      if (current === path) return current;
      window.history.pushState({}, "", path === "/" ? "/" : path);
      return path;
    });
  }, []);

  // Called unconditionally (React hooks rule) but does no network work at all
  // when `route === "/entry"` — see useSession.ts's `enabled` gate. The visitor
  // journey must never depend on, or wait on, an auth check it can never satisfy.
  const canonicalRoute = normalize(route);
  const session = useSession(canonicalRoute !== "/entry");

  if (canonicalRoute === "/entry") {
    return <QuickEntry />;
  }

  if (session.status === "checking") {
    // A brief, unavoidable beat on first load while GET /api/auth/me resolves.
    // Never renders the shell (or any of its data fetches) before this settles.
    return (
      <div className="app-shell-boot">
        <StateMessage tone="loading" title="Loading…" />
      </div>
    );
  }

  if (session.status === "unreachable") {
    // Distinct from `anonymous` on purpose — see useSession.ts. Nothing was signed out;
    // the check simply could not be made, and a login form here would be a dead end that
    // blames the operator for an outage on our side.
    return (
      <div className="app-shell-boot">
        <StateMessage
          tone="error"
          title="Can’t reach EasyFarm right now."
          detail="We couldn’t reach the server to check your sign-in. Nothing has been signed out."
          action={
            <button className="btn btn--primary" onClick={() => window.location.reload()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  if (session.status === "anonymous") {
    return <Login onSubmit={session.login} />;
  }

  // ONE SCREEN, WHATEVER THE PATH. An employee login reaches nothing else in this
  // product — every other route's data is owner-only server-side — so this is not a
  // redirect that could be worked around, it is the client agreeing with the API instead
  // of arguing with it four fetches at a time. `role` is navigation, never the control:
  // forging it in devtools renders the same screen against the same 403s.
  if (session.user?.role === "employee") {
    return <TimeClock onLogout={session.logout} />;
  }

  // ef-047-4 — the check print view, `/payroll/runs/{runId}/print/{lineId}`, deliberately
  // OUTSIDE the app shell: this page carries the first `@media print` stylesheet in the
  // repo (PayRunPrint.css), and printing a nav-bar/sidebar chrome along with a farmer's
  // paycheck is not what any of the buttons in the header are for. Same "no shell"
  // treatment `/time`/`/entry` already get above, for the identical class of reason —
  // checked here, AFTER the employee branch, so an employee login still never reaches
  // anything but TimeClock even if handed this URL directly.
  const printMatch = canonicalRoute.match(/^\/payroll\/runs\/([^/]+)\/print\/([^/]+)$/);
  if (printMatch) {
    // Non-null: a match against a pattern with exactly two capturing groups always
    // populates both — TypeScript's regex typing just can't see that from the pattern.
    return <PayRunPrint runId={printMatch[1]!} lineId={printMatch[2]!} onNavigate={navigate} />;
  }

  // ef-062 anchor - the register check print view, `/books/checks/{entryLineId}`, outside the shell for the same
  // reason as the payroll check above; ef-062-5 replaces the page. A READ: it never allocates a number.
  const registerCheckMatch = canonicalRoute.match(/^\/books\/checks\/([^/]+)$/);
  if (registerCheckMatch) {
    return <RegisterCheckPrint entryLineId={registerCheckMatch[1]!} onNavigate={navigate} />;
  }

  const page =
    canonicalRoute === "/payroll/runs" ? (
      <PayRuns onNavigate={navigate} />
    ) : canonicalRoute === "/payroll/people" ? (
      <H2a onNavigate={navigate} />
    ) : canonicalRoute === "/payroll/returns" ? (
      <YearEnd onNavigate={navigate} initialView={yearEndView} />
    ) : canonicalRoute === "/payroll/leave" ? (
      <PaidLeave />
    ) : canonicalRoute === "/payroll" || canonicalRoute.startsWith("/payroll/") ? (
      <Payroll />
    ) : canonicalRoute === "/reports/hours" ? (
      <Hours onNavigate={navigate} />
    ) : canonicalRoute === "/reports/labour" ? (
      <Labour onNavigate={navigate} />
    ) : canonicalRoute === "/reports/payroll-register" ? (
      <ReportsPayrollRegister />
    ) : canonicalRoute === "/reports/statements" ? (
      <ReportsStatements onNavigate={navigate} />
    ) : canonicalRoute === "/reports/schedule-f" ? (
      <ReportsScheduleF />
    ) : canonicalRoute === "/reports/production-sales" ? (
      <ReportsProductionSales />
    ) : canonicalRoute === "/reports/vendor-purchases" ? (
      <ReportsVendorPurchases />
    ) : canonicalRoute === "/reports/livestock-inventory" ? (
      <ReportsLivestockInventory />
    ) : canonicalRoute === "/reports/crop-production" ? (
      <ReportsCropProduction />
    ) : canonicalRoute === "/reports/cash-flow" ? (
      <ReportsCashFlow />
    ) : canonicalRoute === "/reports/aging" ? (
      <ReportsAging />
    ) : canonicalRoute === "/reports/income-statement" ? (
      // ef-053-0a / ef-053-1 — Spec Architect scaffolding, builder replaces the page.
      <ReportsIncomeStatement />
    ) : canonicalRoute === "/reports/grain-inventory" ? (
      // ef-053-0a / ef-053-2 — Spec Architect scaffolding, builder replaces the page.
      <ReportsGrainInventory />
    ) : canonicalRoute === "/reports/milk-production" ? (
      // ef-053-0a / ef-053-3 — Spec Architect scaffolding, builder replaces the page.
      <ReportsMilkProduction />
    ) : canonicalRoute === "/reports/field-applications" ? (
      // ef-053-0a / ef-053-4 — Spec Architect scaffolding, builder replaces the page.
      <ReportsFieldApplications />
    ) : canonicalRoute === "/reports/partner-splits" ? (
      // ef-053-0a / ef-053-5 — Spec Architect scaffolding, builder replaces the page.
      <ReportsPartnerSplits />
    ) : canonicalRoute === "/reports/partner-splits-dollar" ? (
      // ef-054-0a / ef-054-1 — Spec Architect scaffolding, builder replaces the page.
      <ReportsPartnerSplitsDollar />
    ) : canonicalRoute === "/reports/gl-detail" ? (
      // ef-054-0a / ef-054-2 — Spec Architect scaffolding, builder replaces the page.
      <ReportsGlDetail />
    ) : canonicalRoute === "/reports/trial-balance" ? (
      // ef-054-0a / ef-054-3 — Spec Architect scaffolding, builder replaces the page.
      <ReportsTrialBalance />
    ) : canonicalRoute === "/reports/cash-receipts" ? (
      // ef-054-0a / ef-054-4 — Spec Architect scaffolding, builder replaces the page.
      <ReportsCashReceipts />
    ) : canonicalRoute === "/reports/payroll-tax-liability" ? (
      // ef-054-0a / ef-054-5 — Spec Architect scaffolding, builder replaces the page.
      <ReportsPayrollTaxLiability />
    ) : canonicalRoute === "/reports/activity" ? (
      // ef-056-0a / ef-056-5b (ADR 0010 §1) — Spec Architect scaffolding, ef-056-5b
      // replaces the page. `/books/activity` redirects here — see the anchor comment
      // on the `/books/*` branch below.
      <ReportsActivity />
    ) : canonicalRoute === "/reports/reconciliation" ? (
      // ef-055-0a / ef-055-1 — Spec Architect scaffolding, builder replaces the page.
      <ReportsReconciliation />
    ) : canonicalRoute === "/reports/crop-planting" ? (
      // ef-055-0a / ef-055-2 — Spec Architect scaffolding, builder replaces the page.
      <ReportsCropPlanting />
    ) : canonicalRoute === "/reports" || canonicalRoute.startsWith("/reports/") ? (
      // ef-044-1 (D2): every OTHER /reports/* path — the bare area root and any slug the
      // registry doesn't recognize (`reports-picker.spec.ts`'s own "falls through to the
      // picker, never a blank page") — renders the searchable picker (ADR 0009 §6).
      <ReportsPicker onNavigate={navigate} />
    ) : canonicalRoute === "/setup/places" ? (
      <Places />
    ) : canonicalRoute === "/setup/job-codes" ? (
      <JobCodes />
    ) : canonicalRoute === "/setup/schedule" ? (
      <Schedule />
    ) : canonicalRoute === "/setup/time-off" ? (
      <TimeOff />
    ) : canonicalRoute === "/setup" || canonicalRoute.startsWith("/setup/") ? (
      <Access />
    ) : canonicalRoute === "/books/vendors" ? (
      // ef-042-6 — checked BEFORE the bare-"/books" catch-all below, same pattern every
      // other multi-tab area in this switch already uses (`/payroll/people`,
      // `/reports/hours`, `/setup/schedule`, `/farm/livestock`, ...).
      <BooksVendors />
    ) : canonicalRoute === "/books/recurring" ? (
      <BooksRecurring />
    ) : canonicalRoute === "/books/reconcile" ? (
      <BooksReconcile />
    ) : canonicalRoute === "/books" || canonicalRoute.startsWith("/books/") ? (
      <Books />
    ) : canonicalRoute === "/farm/livestock" ? (
      // ef-041-5 — Farm's first real content tab, checked BEFORE the bare-"/farm"
      // catch-all below, same pattern every other multi-tab area in this switch already
      // uses (`/payroll/people`, `/reports/hours`, `/setup/schedule`, ...).
      <Livestock />
    ) : canonicalRoute === "/farm/fields" ? (
      // ef-045-3 — Farm's third tab, checked BEFORE the bare-"/farm" catch-all below,
      // same pattern the "/farm/livestock" branch directly above already uses.
      <Fields />
    ) : canonicalRoute === "/farm/grain" ? (
      // ef-046-4 — Farm's fourth tab, checked BEFORE the bare-"/farm" catch-all below,
      // same pattern the "/farm/livestock"/"/farm/fields" branches directly above
      // already use.
      <Grain />
    ) : canonicalRoute === "/farm/milk" ? (
      // ef-049-2 — Farm's fifth tab, checked BEFORE the bare-"/farm" catch-all below,
      // same pattern the "/farm/livestock"/"/farm/fields"/"/farm/grain" branches directly
      // above already use.
      <Milk />
    ) : canonicalRoute === "/farm/equipment" ? (
      // ef-061 anchor — equipment is a page under Farm, NOT a sixth tab (Farm is at the ADR 0009
      // five-tab ceiling); ef-061-8 replaces the page. Checked BEFORE the bare-"/farm" catch-all.
      <Equipment />
    ) : canonicalRoute === "/farm" || canonicalRoute.startsWith("/farm/") ? (
      <Farm />
    ) : canonicalRoute === "/time" ? (
      // An OWNER who followed a worker's link. They have no employee record, so there is
      // no time of their own to show — saying so and pointing at the queue beats letting
      // GET /time/mine 403 into a red error a farmer would report as a bug.
      <StateMessage
        tone="empty"
        title="Time entry is for employee logins."
        detail="You review and approve your team's hours on the Payroll screen."
        action={
          <button className="btn btn--primary" onClick={() => navigate("/payroll")}>
            Go to Payroll
          </button>
        }
      />
    ) : canonicalRoute === "/concept/rollup" ? (
      <MultiEntityRollup />
    ) : (
      <Dashboard onNavigate={navigate} onLogout={session.logout} />
    );

  const navKey = navKeyFor(route);

  return (
    <AppShell active={navKey} onNavigate={navigate} onLogout={session.logout}>
      {page}
    </AppShell>
  );
}
